// src/Pages/ReporteToPdfStatic.js
import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import html2pdf from "html2pdf.js";
import useReporteSnapshot from "../hooks/useReporteSnapshot";
import ReporteStaticHTML from "../components/reportes/ReporteStaticHTML";

// ------- utils -------
function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

async function waitImagesLoaded(rootEl, timeoutMs = 15000) {
    if (!rootEl) return;
    const imgs = Array.from(rootEl.querySelectorAll("img")).filter((i) => !i.complete);
    if (imgs.length === 0) return;

    await Promise.race([
        Promise.all(
            imgs.map(
                (img) =>
                    new Promise((res) => {
                        const done = () => {
                            img.removeEventListener("load", done);
                            img.removeEventListener("error", done);
                            res();
                        };
                        img.addEventListener("load", done);
                        img.addEventListener("error", done);
                    })
            )
        ),
        new Promise((res) => setTimeout(res, timeoutMs)),
    ]);
}

function exportReportPdf(rootEl, filename = "reporte.pdf") {
    if (!rootEl) return Promise.resolve();
    return html2pdf()
        .set({
            filename,
            margin: [20, 14], // mm (top/bottom, left/right)
            pagebreak: {
                mode: ["css", "legacy"],
                before: [".page-start"], // inicio forzado
                after: [".force-break", ".html2pdf__page-break"], // salto después del marcador
                avoid: [".avoid-break"], // intenta no cortar dentro
            },
            html2canvas: {
                scale: 2,
                useCORS: true,
                backgroundColor: "#fff",
                logging: false,
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(rootEl)
        .save();
}

// ------- componente -------
export default function ReporteToPdfStatic() {
    const { contratoId } = useParams();
    const q = useQuery();
    const auto = q.get("auto") === "1";

    const { loading, error, snapshot } = useReporteSnapshot(contratoId);
    const filename = `reporte-servicio-${contratoId || "sin-id"}.pdf`;

    const targetRef = useRef(null);

    const doDownload = useCallback(async () => {
        if (!targetRef.current) return;
        // asegurar render + carga de imágenes/mapas
        await new Promise((r) => setTimeout(r, 30));
        await waitImagesLoaded(targetRef.current, 15000);
        await exportReportPdf(targetRef.current, filename);
    }, [filename]);

    // AUTO
    useEffect(() => {
        if (!auto) return;
        if (loading || error || !snapshot) return;

        let cancelled = false;
        (async () => {
            try {
                await doDownload();
                if (!cancelled) {
                    window.parent?.postMessage({ type: "PDF_READY", id: contratoId, ok: true }, "*");
                }
            } catch (e) {
                if (!cancelled) {
                    window.parent?.postMessage({ type: "PDF_READY", id: contratoId, ok: false, error: String(e) }, "*");
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [auto, loading, error, snapshot, doDownload, contratoId]);

    // Manual
    if (!auto) {
        return (
            <div style={{ padding: 16 }}>
                {loading && "Cargando snapshot..."}
                {error && <div style={{ color: "#b00", fontWeight: 600 }}>{String(error)}</div>}

                {!loading && !error && snapshot && (
                    <>
                        <button
                            onClick={doDownload}
                            style={{
                                marginBottom: 16,
                                background: "#1b5e20",
                                border: "1px solid #2e7d32",
                                color: "#fff",
                                borderRadius: 8,
                                padding: "8px 12px",
                                cursor: "pointer",
                                fontSize: 14,
                                fontWeight: 600,
                            }}
                        >
                            Descargar PDF
                        </button>

                        {/* html2pdf captura este DOM */}
                        <div ref={targetRef} style={{ background: "#fff", maxWidth: 800, margin: "0 auto" }}>
                            <ReporteStaticHTML data={snapshot} />
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Auto oculto
    return (
        <div
            style={{
                position: "fixed",
                left: -10000,
                top: 0,
                width: 900,
                background: "#fff",
                zIndex: -1,
            }}
        >
            {!loading && !error && snapshot && (
                <div ref={targetRef}>
                    <ReporteStaticHTML data={snapshot} />
                </div>
            )}
        </div>
    );
}
